import { TrendingUp, Users } from 'lucide-react';

// Statuses that occupied a seat at some point of the day. "completed" (incl.
// released tables) is intentionally counted so the day's peak/totals do not
// shrink as guests leave; only truly empty bookings are excluded.
const COUNTED = new Set(['pending', 'confirmed', 'arrived', 'seated', 'completed']);
const SLOT_OPTIONS = [15, 30, 45, 60];

const toMin = (hm) => {
  const [h, m] = (hm || '0:0').split(':').map(Number);
  return h * 60 + m;
};

const minToHm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// Colour by load ratio: calm green → busy amber → full red
function loadColor(ratio) {
  if (ratio >= 0.85) return '#EF4444';
  if (ratio >= 0.6) return '#F97316';
  if (ratio >= 0.35) return '#EAB308';
  return '#22C55E';
}

function loadLabel(ratio) {
  if (ratio >= 0.85) return 'Voll';
  if (ratio >= 0.6) return 'Stark';
  if (ratio >= 0.35) return 'Mäßig';
  return 'Ruhig';
}

/**
 * Live capacity / occupancy view for the selected day.
 * - Per window (Mittag/Abend): peak concurrent guests vs. total seats.
 * - Per time-slot table: how many GUESTS arrive at each start time (online +
 *   manual together), grouped by a configurable slot raster.
 * Computed purely client-side from already-loaded reservations + tables.
 */
export function OccupancyForecast({ reservations = [], tables = [], slotMinutes = 15, onSlotMinutesChange }) {
  const totalSeats = tables
    .filter((t) => t.active && t.status_override !== 'blocked')
    .reduce((s, t) => s + (Number(t.max_persons) || 0), 0);

  if (totalSeats === 0) return null;

  const active = reservations.filter((r) => COUNTED.has(r.status) && r.start_time && r.end_time);

  // Hour range used for the window peak figures.
  let fromH = 11;
  let toH = 23;
  if (active.length) {
    const startHs = active.map((r) => Math.floor(toMin(r.start_time) / 60));
    const endHs = active.map((r) => Math.ceil(toMin(r.end_time) / 60));
    fromH = Math.min(11, ...startHs);
    toH = Math.max(22, ...endHs);
  }
  fromH = Math.max(8, fromH);
  toH = Math.min(23, toH);

  // Concurrent guests present during each hour [h:00, h+1:00) → window peaks
  const hourGuests = [];
  for (let h = fromH; h <= toH; h++) {
    const hs = h * 60;
    const he = (h + 1) * 60;
    const guests = active.reduce((sum, r) => {
      const s = toMin(r.start_time);
      const e = toMin(r.end_time);
      return s < he && e > hs ? sum + (Number(r.persons) || 0) : sum;
    }, 0);
    hourGuests.push({ hour: h, guests });
  }
  const windowPeak = (lo, hi) =>
    hourGuests.filter((x) => x.hour >= lo && x.hour < hi).reduce((m, x) => Math.max(m, x.guests), 0);
  const windows = [
    { key: 'lunch', label: 'Mittag', sub: 'bis 16 Uhr', peak: windowPeak(fromH, 16) },
    { key: 'dinner', label: 'Abend', sub: 'ab 16 Uhr', peak: windowPeak(16, toH + 1) },
  ];

  // ===== Guests per arrival time-slot (configurable raster) =====
  const interval = SLOT_OPTIONS.includes(Number(slotMinutes)) ? Number(slotMinutes) : 15;
  const slotMap = {};
  active.forEach((r) => {
    const slot = Math.floor(toMin(r.start_time) / interval) * interval;
    if (!slotMap[slot]) slotMap[slot] = { guests: 0, count: 0 };
    slotMap[slot].guests += Number(r.persons) || 0;
    slotMap[slot].count += 1;
  });
  const slotRows = Object.entries(slotMap)
    .map(([m, v]) => ({ min: Number(m), ...v }))
    .filter((row) => row.guests > 0)
    .sort((a, b) => a.min - b.min);
  const maxSlotGuests = Math.max(1, ...slotRows.map((r) => r.guests));

  return (
    <div
      className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden mb-6"
      data-testid="occupancy-forecast"
    >
      <div className="px-4 md:px-6 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
        <h2 className="font-display text-lg text-[#1C1C19] flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#5C5C59]" /> Auslastung
        </h2>
        <span className="text-xs text-[#5C5C59] flex items-center gap-1" data-testid="occupancy-total-seats">
          <Users className="w-3.5 h-3.5" /> {totalSeats} Plätze gesamt
        </span>
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {windows.map((w) => {
          const ratio = totalSeats ? Math.min(1, w.peak / totalSeats) : 0;
          const pct = Math.round(ratio * 100);
          const color = loadColor(ratio);
          return (
            <div
              key={w.key}
              className="border border-[#E5E5E0] rounded-lg p-3"
              data-testid={`occupancy-window-${w.key}`}
            >
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-medium text-sm text-[#1C1C19]">{w.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#5C5C59]">{w.sub}</span>
                </div>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wider"
                  style={{ background: color + '22', color }}
                  data-testid={`occupancy-window-${w.key}-badge`}
                >
                  {loadLabel(ratio)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5 mb-1.5">
                <span className="font-display text-2xl leading-none" style={{ color }} data-testid={`occupancy-window-${w.key}-value`}>
                  {w.peak}
                </span>
                <span className="text-xs text-[#5C5C59]">/ {totalSeats} Plätze (Spitze)</span>
                <span className="text-xs text-[#5C5C59] ml-auto">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#F1F1ED] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Guests per arrival time-slot */}
      <div className="px-4 md:px-6 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Gäste je Uhrzeit (Ankunft)</span>
          <label className="flex items-center gap-1.5 text-xs text-[#5C5C59]">
            Raster
            <select
              value={interval}
              onChange={(e) => onSlotMinutesChange && onSlotMinutesChange(Number(e.target.value))}
              data-testid="occupancy-slot-select"
              className="border border-[#E5E5E0] rounded px-1.5 py-1 text-xs text-[#1C1C19] focus:outline-none focus:border-[#1C1C19] bg-white"
            >
              {SLOT_OPTIONS.map((o) => (
                <option key={o} value={o}>{o} Min</option>
              ))}
            </select>
          </label>
        </div>

        {slotRows.length === 0 ? (
          <div className="text-xs text-[#5C5C59] py-4 text-center" data-testid="occupancy-slot-empty">
            Noch keine Reservierungen für diesen Tag.
          </div>
        ) : (
          <div className="border border-[#E5E5E0] rounded-lg overflow-hidden" data-testid="occupancy-slot-table">
            <div className="flex items-center px-3 py-1.5 bg-[#F9F9F7] text-[10px] uppercase tracking-wider text-[#5C5C59]">
              <span className="w-16 shrink-0">Uhrzeit</span>
              <span className="flex-1">Gäste</span>
              <span className="w-10 text-right shrink-0">Res.</span>
            </div>
            {slotRows.map((row) => {
              const barPct = Math.round((row.guests / maxSlotGuests) * 100);
              const ratio = totalSeats ? Math.min(1, row.guests / totalSeats) : 0;
              const color = loadColor(ratio);
              return (
                <div
                  key={row.min}
                  className="flex items-center px-3 py-1.5 border-t border-[#F1F1ED]"
                  data-testid={`occupancy-slot-row-${minToHm(row.min)}`}
                >
                  <span className="w-16 shrink-0 font-mono text-sm text-[#1C1C19]">{minToHm(row.min)}</span>
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="flex-1 h-3 rounded-full bg-[#F1F1ED] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, background: color }} />
                    </div>
                    <span className="font-display text-sm w-7 text-right shrink-0" style={{ color }} data-testid={`occupancy-slot-guests-${minToHm(row.min)}`}>
                      {row.guests}
                    </span>
                  </div>
                  <span className="w-10 text-right shrink-0 text-xs text-[#5C5C59]">{row.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
